import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { getEarliestReleaseDate } from '../../lib/distributionService';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function DistributionDatePicker({
  label = 'Release Date',
  value,
  onChange,
  minDate = getEarliestReleaseDate(),
  required = true,
  error = '',
  id = 'dist-release-date',
  showLabelIcon = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Parse initial view date
  const selectedDateObj = value ? new Date(value + 'T00:00:00') : new Date(minDate + 'T00:00:00');
  const [viewYear, setViewYear] = useState(
    !isNaN(selectedDateObj.getFullYear()) ? selectedDateObj.getFullYear() : new Date().getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    !isNaN(selectedDateObj.getMonth()) ? selectedDateObj.getMonth() : new Date().getMonth()
  );

  const minDateObj = new Date(minDate + 'T00:00:00');
  minDateObj.setHours(0, 0, 0, 0);

  const todayObj = new Date();
  todayObj.setHours(0, 0, 0, 0);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  // Generate calendar days
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay();

  const handleSelectDate = (day) => {
    const monthStr = String(viewMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const formatted = `${viewYear}-${monthStr}-${dayStr}`;
    onChange(formatted);
    setIsOpen(false);
  };

  // Format display string
  const formatDisplayDate = (val) => {
    if (!val) return 'Select release date...';
    try {
      const d = new Date(val + 'T00:00:00');
      if (isNaN(d.getTime())) return val;
      return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    } catch {
      return val;
    }
  };

  return (
    <div className="dist-field-group" ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label className="dist-label" htmlFor={id}>
          {showLabelIcon && <CalendarIcon size={14} color="#d9fb06" style={{ display: 'inline', marginRight: 4 }} />}
          {label} {required && <span className="dist-required-mark">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        className={`dist-custom-select-trigger ${isOpen ? 'active' : ''} ${error ? 'error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className={value ? 'dist-select-text' : 'dist-select-placeholder'}>
          {formatDisplayDate(value)}
        </span>
        <CalendarIcon size={18} color="#d9fb06" />
      </button>

      {error && <span className="dist-field-error">{error}</span>}

      {/* Calendar Overlay Popover */}
      {isOpen && (
        <div className="dist-calendar-sheet" role="dialog" aria-label="Release Date Calendar">
          {/* Header Month / Year & Arrows */}
          <div className="dist-calendar-header">
            <button
              type="button"
              className="dist-cal-nav-btn"
              onClick={handlePrevMonth}
              title="Previous Month"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="dist-calendar-title">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </div>

            <button
              type="button"
              className="dist-cal-nav-btn"
              onClick={handleNextMonth}
              title="Next Month"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Weekday Labels */}
          <div className="dist-calendar-grid weekdays">
            {WEEKDAY_NAMES.map((w) => (
              <div key={w} className="dist-cal-weekday">
                {w}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="dist-calendar-grid days">
            {/* Empty prefix slots */}
            {Array.from({ length: firstDayIndex }).map((_, i) => (
              <div key={`empty-${i}`} className="dist-cal-day empty" />
            ))}

            {/* Days in Month */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const cellDate = new Date(viewYear, viewMonth, day);
              cellDate.setHours(0, 0, 0, 0);

              const isDisabled = cellDate < minDateObj;
              const isToday = cellDate.getTime() === todayObj.getTime();

              const monthStr = String(viewMonth + 1).padStart(2, '0');
              const dayStr = String(day).padStart(2, '0');
              const dateIso = `${viewYear}-${monthStr}-${dayStr}`;
              const isSelected = value === dateIso;

              return (
                <button
                  key={day}
                  type="button"
                  disabled={isDisabled}
                  className={`dist-cal-day ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                  onClick={() => !isDisabled && handleSelectDate(day)}
                  title={isDisabled ? 'Disabled (Must be at least 7 days from submission)' : `Select ${day} ${MONTH_NAMES[viewMonth]}`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Calendar Footer Rule Note */}
          <div className="dist-cal-footer-note">
            <span style={{ color: '#d9fb06', fontWeight: 600 }}>7-Day DSP Inspection Rule:</span> Releases must be scheduled at least 7 days out for playlist curation and metadata ingestion.
          </div>
        </div>
      )}
    </div>
  );
}
